import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  label?: string;
  showStrengthMeter?: boolean;
  className?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  value,
  onChange,
  placeholder = "Enter password",
  required = true,
  minLength = 6,
  label = "Password",
  showStrengthMeter = true,
  className = ""
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    checkPasswordStrength(e.target.value);
  };

  const checkPasswordStrength = (password: string) => {
    if (password.length < minLength) {
      setPasswordStrength('weak');
      return;
    }

    let score = 0;
    // Check for uppercase letters
    if (/[A-Z]/.test(password)) score++;
    // Check for lowercase letters
    if (/[a-z]/.test(password)) score++;
    // Check for numbers
    if (/[0-9]/.test(password)) score++;
    // Check for special characters
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score >= 3) {
      setPasswordStrength('strong');
    } else if (score >= 2) {
      setPasswordStrength('medium');
    } else {
      setPasswordStrength('weak');
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          id={id}
          value={value}
          onChange={handlePasswordChange}
          required={required}
          minLength={minLength}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 pr-10 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      
      {showStrengthMeter && value && (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <div className="h-2 flex-1 bg-zinc-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getPasswordStrengthColor()}`} 
                style={{ 
                  width: passwordStrength === 'weak' ? '33%' : 
                         passwordStrength === 'medium' ? '66%' : '100%' 
                }}
              ></div>
            </div>
            <span className="text-xs text-gray-400">
              {passwordStrength === 'weak' ? 'Weak' : 
               passwordStrength === 'medium' ? 'Medium' : 'Strong'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Use at least {minLength} characters with a mix of letters, numbers, and symbols
          </p>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;